import * as React from 'react';
import { Check, ChevronsUpDown, X, MapPin, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from './badge';

/**
 * AssetMultiSelect - A searchable multi-select component for assets
 *
 * Searches across:
 * - Manufacturer name (from manufacturer relationship)
 * - Model name (from model relationship)
 * - Serial number
 * - Location name (from location relationship)
 *
 * @param {Object} props
 * @param {Array} props.assets - Array of asset objects with relationships
 * @param {Array} props.value - Array of selected asset IDs
 * @param {Function} props.onChange - Callback when selection changes, receives array of asset IDs
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disable the component
 * @param {Array} props.skip - Array of asset IDs to disable/skip
 */
export function AssetMultiSelect({
	assets = [],
	value = [],
	onChange,
	placeholder = 'Search assets...',
	disabled = false,
	skip = [],
	className,
}) {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const containerRef = React.useRef(null);
	const inputRef = React.useRef(null);

	// Close dropdown when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Get asset details for display
	const getAssetDisplayInfo = (asset) => {
		const manufacturer = asset.manufacturer?.name || asset.manufacturerName || '';
		const model = asset.model?.name || asset.modelName || '';
		const serial = asset.serialNumber || '';
		const location = asset.location?.name || '';

		return {
			manufacturer,
			model,
			serial,
			location,
			displayName: `${manufacturer} ${model}`.trim(),
			fullDisplay: `${manufacturer} ${model} (S/N: ${serial})${location ? ` - ${location}` : ''}`,
		};
	};

	// Custom filter function that searches across all relevant fields
	const filterAssets = (asset, searchTerm) => {
		if (!searchTerm) return true;

		const term = searchTerm.toLowerCase();
		const manufacturer = (asset.manufacturer?.name || asset.manufacturerName || '').toLowerCase();
		const model = (asset.model?.name || asset.modelName || '').toLowerCase();
		const serial = (asset.serialNumber || '').toLowerCase();
		const location = (asset.location?.name || '').toLowerCase();
		const tag = (asset.tag || '').toLowerCase();

		return (
			manufacturer.includes(term) ||
			model.includes(term) ||
			serial.includes(term) ||
			location.includes(term) ||
			tag.includes(term)
		);
	};

	// Filter and group assets
	const filteredAssets = React.useMemo(() => {
		return (assets || []).filter((asset) => filterAssets(asset, search));
	}, [assets, search]);

	// Group filtered assets by manufacturer
	const groupedAssets = React.useMemo(() => {
		const groups = {};
		filteredAssets.forEach((asset) => {
			const manufacturer = asset.manufacturer?.name || asset.manufacturerName || 'Other';
			if (!groups[manufacturer]) {
				groups[manufacturer] = [];
			}
			groups[manufacturer].push(asset);
		});
		return groups;
	}, [filteredAssets]);

	// Get selected assets for display
	const selectedAssets = React.useMemo(() => {
		return (assets || []).filter((asset) => value.includes(asset.id));
	}, [assets, value]);

	const handleSelect = (assetId) => {
		const newValue = value.includes(assetId)
			? value.filter((id) => id !== assetId)
			: [...value, assetId];
		onChange(newValue);
	};

	const handleRemove = (assetId, e) => {
		e.stopPropagation();
		onChange(value.filter((id) => id !== assetId));
	};

	const handleClearAll = (e) => {
		e.stopPropagation();
		onChange([]);
	};

	const handleTriggerClick = () => {
		if (!disabled) {
			setOpen(!open);
			if (!open) {
				setTimeout(() => inputRef.current?.focus(), 0);
			}
		}
	};

	return (
		<div className={cn('w-full relative', className)} ref={containerRef}>
			{/* Trigger button */}
			<button
				type="button"
				role="combobox"
				aria-expanded={open}
				onClick={handleTriggerClick}
				className={cn(
					'flex min-h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
					selectedAssets.length > 0 && 'h-auto'
				)}
				disabled={disabled}
			>
				<div className="flex flex-1 flex-wrap gap-1.5">
					{selectedAssets.length === 0 ? (
						<span className="text-gray-400">{placeholder}</span>
					) : (
						selectedAssets.map((asset) => {
							const info = getAssetDisplayInfo(asset);
							return (
								<Badge
									key={asset.id}
									variant="secondary"
									className="flex items-center gap-1 bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
								>
									<span className="max-w-[200px] truncate">
										{info.displayName} ({info.serial})
									</span>
									<button
										type="button"
										className="ml-1 rounded-full outline-none ring-offset-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
										onClick={(e) => handleRemove(asset.id, e)}
									>
										<X className="h-3 w-3 text-indigo-600 hover:text-indigo-800" />
									</button>
								</Badge>
							);
						})
					)}
				</div>
				<div className="flex items-center gap-1 ml-2">
					{selectedAssets.length > 0 && (
						<button
							type="button"
							className="rounded-full p-0.5 hover:bg-gray-100"
							onClick={handleClearAll}
						>
							<X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
						</button>
					)}
					<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
				</div>
			</button>

			{/* Dropdown */}
			{open && (
				<div className="absolute z-[100] mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
					{/* Search input */}
					<div className="flex items-center border-b px-3">
						<Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
						<input
							ref={inputRef}
							type="text"
							className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-gray-400"
							placeholder="Search by manufacturer, model, serial, or location..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>

					{/* Asset list */}
					<div className="max-h-[300px] overflow-y-auto p-1">
						{filteredAssets.length === 0 ? (
							<p className="py-6 text-center text-sm text-gray-500">No assets found.</p>
						) : (
							Object.entries(groupedAssets).map(([manufacturer, manufacturerAssets]) => (
								<div key={manufacturer} className="mb-2">
									<div className="px-2 py-1.5 text-xs font-medium text-gray-500">
										{manufacturer}
									</div>
									{manufacturerAssets.map((asset) => {
										const info = getAssetDisplayInfo(asset);
										const isSelected = value.includes(asset.id);
										const isDisabled = skip.includes(asset.id);

										return (
											<button
												key={asset.id}
												type="button"
												onClick={() => !isDisabled && handleSelect(asset.id)}
												disabled={isDisabled}
												className={cn(
													'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100',
													isDisabled && 'opacity-50 cursor-not-allowed'
												)}
											>
												<div
													className={cn(
														'flex h-4 w-4 items-center justify-center rounded border',
														isSelected
															? 'border-indigo-600 bg-indigo-600 text-white'
															: 'border-gray-300'
													)}
												>
													{isSelected && <Check className="h-3 w-3" />}
												</div>
												<div className="flex flex-1 flex-col text-left">
													<div className="flex items-center gap-2">
														<span className="font-medium">{info.model || 'Unknown Model'}</span>
														<span className="text-xs text-gray-500">
															S/N: {info.serial || 'N/A'}
														</span>
													</div>
													{info.location && (
														<div className="flex items-center gap-1 text-xs text-gray-500">
															<MapPin className="h-3 w-3" />
															<span>{info.location}</span>
														</div>
													)}
												</div>
											</button>
										);
									})}
								</div>
							))
						)}
					</div>
				</div>
			)}

			{/* Count indicator */}
			{selectedAssets.length > 0 && (
				<p className="mt-1.5 text-xs text-gray-500">
					{selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected
				</p>
			)}
		</div>
	);
}

export default AssetMultiSelect;
